import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles, FormHelperText } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { validateInput } from './ValidateInput';
import WarningIcon from '@/assets/images/warning.svg';
// text || number
// label = string
// size = 'large' || 'medium' || 'small'
// prefixContent = string
// label = string
// type = 'text' || 'number'

const useStyles = makeStyles((theme) => ({
  root: {},
  large: {
    '& .MuiInput-underline': {
      '&::after': {
        width: '100%',
      },
      '&::before': {
        width: '100%',
      },
    },
    '& input': {
      width: '420px',
      '&::placeholder': {
        color: '#888888',
        opacity: '1',
      },
    },
  },
  medium: {
    '& .MuiInput-underline': {
      '&::after': {
        width: '190px',
        '@media (max-width: 1024px)': {
          width: '100%',
        },
      },
      '&::before': {
        width: '190px',
        '@media (max-width: 1024px)': {
          width: '100%',
        },
      },
    },
    '& input': {
      width: '190px',
      '@media (max-width: 1024px)': {
        width: '100%',
      },
    },
  },
  small: {
    '& .MuiInput-underline': {
      '&::after': {
        width: '80px',
      },
      '&::before': {
        width: '80px',
      },
    },
    '& input': {
      width: '80px',
    },
  },
  inputComponent: {
    width: '100%',
    '& .MuiSelect-root': {
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiFormLabel-root': {
      color: theme.palette.$inputColor,
      transform: 'none',
      fontSize: '14px',
      fontWeight: 'normal',
    },
    '& .MuiFilledInput-underline': {
      fontSize: '18px',
      color: theme.palette.$inputColor,
      '&::after': {
        borderBottom: `1px solid ${theme.palette.$clearBlue}`,
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: `2px solid ${theme.palette.$lineGrey}`,
      },
      '&:hover::before': {
        borderBottom: `1px solid ${theme.palette.$lineGrey}`,
      },
      '&::before': {
        borderBottom: `1px solid ${theme.palette.$lineGrey}`,
      },
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: '10px',
      '& .MuiTypography-body1': {
        fontSize: '18px',
      },
    },
    '& input::-webkit-inner-spin-button': {
      webkitAppearance: 'none',
    },
    'input::-webkit-outer-spin-button': {
      webkitAppearance: 'none',
    },
    '& .MuiInputLabel-filled': {
      transform: 'translate(8px, 16px) scale(1)',
      fontSize: '16px',
      lineHeight: '24px',
      '&.MuiInputLabel-shrink': {
        color: '#575D65',
        fontSize: '12px',
        lineHeight: '16px',
        transform: 'translate(8px, 8px)',
      },
    },
    '& .MuiInputBase-input': {
      height: '24px',
      padding: '24px 8px 8px 8px',
      color: theme.palette.$inputColor,
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 600,
    },
    '& .MuiInputLabel-underline': {
      '&::after': {
        borderBottom: '2px solid #005EFD',
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: `2px solid ${theme.palette.$lineGrey}`,
      },
      '&:hover::before': {
        borderBottom: `1px solid ${theme.palette.$lineGrey}`,
      },
      '&::before': {
        borderBottom: `1px solid ${theme.palette.$lineGrey}`,
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#F7F8F9',
    },
    '& .MuiFormHelperText-contained': {
      margin: 0,
      padding: '8px',
      fontSize: '12px',
      lineHeight: '16px',
      color: '#575D65',
    },
  },
  helperTextComponent: {
    fontSize: '12px',
    lineHeight: '16px',
    marginLeft: '8px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'start',
  },
  validValue: {
    '& .MuiInput-underline': {
      '&::before': {
        borderBottom: '1px solid #cfcfcf',
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: '1px solid #cfcfcf',
      },
    },
  },
  errorType: {
    '& .MuiFilledInput-input': {
      fontSize: '18px',
      color: theme.palette.$inputColor,
    },
    '& .MuiFilledInput-underline': {
      '&::after': {
        borderBottom: '2px solid red',
        transform: 'scale(1)',
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.$lipstick,
    },
  },
  iconWarning: {
    verticalAlign: 'middle',
    marginBottom: '2px',
    marginRight: '4px',
  },
  inputCode: {
    width: '40px',
  },
  paddingRight: {
    '& .MuiInputBase-input': {
      paddingRight: '30px',
    },
  },
}));

const InputComponent = ({
  size,
  prefixContent,
  message,
  errorText,
  setErrorText,
  value,
  kind,
  getValue,
  type,
  name,
  maxLength,
  preventSpecialCharacters,
  leadingZeros,
  checkValueDate,
  checkValueMonth,
  valueInputCode,
  checkValueYear,
  paddingRight,
  autoFocus,
  checkOnChange,
  ...props
}) => {
  const classes = useStyles();
  const [valueInput, setValueInput] = useState('');

  const [dynamicContent] = useState(
    useSelector((state) => state.form && state.form.form && state.form.form.dynamicContent),
  );

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    if (value) {
      setValueInput(value);
    } else {
      setValueInput('');
    }
  }, [value]);


  // eslint-disable-next-line no-shadow
  const validateValue = (value) => {
    const err = validateInput(kind, value.target.value, dynamicContent);
    if (kind !== 'confirmPassword') {
      if (err) {
        // console.log('error', err);
        setErrorText(err);
      } else {
        setErrorText('');
      }
    }
  };

  return (
    <>
      {type === 'number' && (
        <TextField
          className={clsx(
            classes.inputComponent,
            classes[size],
            errorText ? classes.errorType : '',
            valueInput ? classes.validValue : '',
            paddingRight && classes.paddingRight,
          )}
          InputProps={{
            startAdornment: prefixContent && (
              <InputAdornment position="start">{prefixContent}</InputAdornment>
            ),
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{
            maxLength:
              valueInputCode === '65'
                || valueInputCode === ''
                || kind === 'country_code'
                ? maxLength
                : 15,
            pattern: '[0-9]*',
            inputMode: 'number',
            name,
          }}
          onBlur={(e) => {
            leadingZeros(e.target);
            checkValueDate(e.target);
            checkValueMonth(e.target);
            checkValueYear(e.target);
            setValueInput(e.target.value);
            autoFocus(e.target, maxLength);
            getValue({ value: e.target.value, name: e.target.name, error: validateInput(kind, e.target.value) });
            if (valueInputCode === '65' || valueInputCode === '') {
              validateValue(e);
            } else {
              setErrorText('');
            }
          }}
          helperText={errorText}
          value={valueInput}
          name={name}
          onChange={(e) => {
            setValueInput(e.target.value);
            autoFocus(e.target, maxLength);
            if (checkOnChange) {
              validateValue(e);
              getValue({ value: e.target.value, name: e.target.name, error: validateInput(kind, e.target.value) });
            }
          }}
          onKeyPress={preventSpecialCharacters}
          tabIndex={-1}
          {...props}
        />
      )}
      {(type === 'text' || type === 'password') && (
        <>
          <TextField
            className={clsx(
              classes.inputComponent,
              classes[size],
              errorText ? classes.errorType : '',
              valueInput ? classes.validValue : '',
              paddingRight && classes.paddingRight,
            )}
            inputProps={{
              maxLength,
              type,
              name,
            }}
            onBlur={(e) => {
              getValue({ value: e.target.value, name: e.target.name, error: validateInput(kind, e.target.value) });
              validateValue(e);
              autoFocus(e.target, maxLength);
            }}
            name={name}
            // helperText={errorText || message}
            value={valueInput}
            // tabIndex={-1}
            onChange={(e) => {
              setValueInput(e.target.value);
              autoFocus(e.target, maxLength);
              if (checkOnChange) {
                validateValue(e);
                getValue({ value: e.target.value, name: e.target.name, error: validateInput(kind, e.target.value) });
              }
            }}
            onKeyPress={preventSpecialCharacters}
            {...props}
            variant="filled"
          />
          {errorText && (
            <FormHelperText className={classes.helperTextComponent} error={!!errorText}>
              <img src={WarningIcon} className={classes.iconWarning} alt="#" />
              <span>
                {errorText}
              </span>
            </FormHelperText>
          )}
          {!errorText && (
            <FormHelperText className={classes.helperTextComponent}>{message}</FormHelperText>
          )}
        </>

      )}
    </>
  );
};

InputComponent.defaultProps = {
  size: '',
  prefixContent: '',
  type: '',
  label: '',
  kind: '',
  message: '',
  name: '',
  maxLength: 100,
  getValue: _ => _,
  preventSpecialCharacters: _ => _,
  leadingZeros: _ => _,
  checkValueDate: _ => _,
  checkValueMonth: _ => _,
  checkValueYear: _ => _,
  autoFocus: _ => _,
  valueInputCode: '',
  paddingRight: false,
  checkOnChange: false,
};

InputComponent.propTypes = {
  size: PropTypes.string, // 'large' || 'medium' || 'small'
  prefixContent: PropTypes.string,
  type: PropTypes.string, // 'text' || 'number'
  label: PropTypes.string,
  message: PropTypes.string,
  kind: PropTypes.string, // phone || email || code || unit_max_2 || unit_max_3 || block_number
  name: PropTypes.string,
  maxLength: PropTypes.number,
  getValue: PropTypes.func, // return {name,value},
  preventSpecialCharacters: PropTypes.func,
  valueInputCode: PropTypes.string,
  leadingZeros: PropTypes.func,
  checkValueDate: PropTypes.func,
  checkValueMonth: PropTypes.func,
  checkValueYear: PropTypes.func,
  paddingRight: PropTypes.bool,
  autoFocus: PropTypes.func,
  checkOnChange: PropTypes.bool,
};
export default InputComponent;
